import { useCompaniesById } from "@services";
import { use, useMemo } from "react";
import { UNSAFE_DataRouterNavigationContext, useParams } from "react-router";
import { buildCompanyPath, resolveActiveCompanySlug } from "./companyPath";

type UseCompanyPathResult = {
  /**
   * The active company slug. Sourced from the bare-root `:slug` route param when
   * present, otherwise derived from the current session company's detail.
   * `undefined` until one resolves.
   */
  slug?: string;
  /** Build a slug-relative path, e.g. `buildPath("/agents")` → `/<slug>/agents`. */
  buildPath: (to: string) => string;
};

/**
 * Exposes the active company slug plus a slug-relative path builder. Slug routes
 * read the URL param as the tenant source of truth; legacy unprefixed routes
 * fall back to the current session company's detail so their next internal
 * navigation moves onto the canonical bare-root `/:slug` route. While neither
 * source has resolved, `buildPath` safely degrades to a plain absolute path.
 */
export const useCompanyPath = (): UseCompanyPathResult => {
  const { slug: slugParam } = useParams<{ slug?: string }>();
  const { data: company } = useCompaniesById();

  // Prefer the pending navigation's slug over the still-committed URL param so
  // links built mid-switch don't revert the company (see resolveActiveCompanySlug).
  // Read the pending location off the data-router navigation context directly
  // rather than via `useNavigation()`, which invariants outside a data router:
  // this hook also renders under plain `MemoryRouter` (component tests), where
  // the context is null and there is simply no pending navigation to honor.
  const routerNavigation = use(UNSAFE_DataRouterNavigationContext);
  const slug = resolveActiveCompanySlug(
    routerNavigation?.navigation.location?.pathname,
    slugParam,
    company?.slug,
  );

  return useMemo(
    () => ({
      slug,
      buildPath: (to: string) => buildCompanyPath(slug, to),
    }),
    [slug],
  );
};
